import type { GroceryConversationPrompt } from './inspiration';
import { normalizeInspirationQuestion } from './inspiration';

// A browser/device history, not an IP-address identifier. Different visitors
// behind the same network must not share a question history.
const STORAGE_KEY = 'youdle:seen-conversation-prompts:v1';
const MAX_HISTORY = 400;

export function readSeenConversationQuestions(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    if (!value) return [];
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed)
      ? parsed.filter((item): item is string => typeof item === 'string' && item.length < 250).slice(-MAX_HISTORY)
      : [];
  } catch {
    return [];
  }
}

export function markConversationQuestionSeen(question: string): void {
  if (typeof window === 'undefined') return;
  const key = normalizeInspirationQuestion(question);
  if (!key) return;
  try {
    const previous = readSeenConversationQuestions();
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify([...previous.filter(item => item !== key), key].slice(-MAX_HISTORY)),
    );
  } catch {
    // Browsers that block storage can still browse and rotate questions.
  }
}

function shuffle<T>(items: readonly T[], random: () => number): T[] {
  const result = [...items];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/**
 * Draw from the entire server-approved pool. Prioritize questions that this
 * browser has not seen, and shuffle both halves so page loads and hour changes
 * never pin everyone to the first API item. The API excludes claimed prompts;
 * do not silently re-add client-side editorial prompts after that filtering.
 */
export function orderConversationQuestions(
  candidates: readonly GroceryConversationPrompt[],
  seenQuestions: readonly string[] = readSeenConversationQuestions(),
  random: () => number = Math.random,
): GroceryConversationPrompt[] {
  const unique: GroceryConversationPrompt[] = [];
  const keys = new Set<string>();
  for (const item of candidates) {
    if (typeof item?.question !== 'string') continue;
    const key = normalizeInspirationQuestion(item.question);
    if (!key || keys.has(key)) continue;
    keys.add(key);
    unique.push(item);
  }

  const seen = new Set(seenQuestions.map(normalizeInspirationQuestion));
  const unseen = shuffle(unique.filter(item => !seen.has(normalizeInspirationQuestion(item.question))), random);
  const previouslySeen = shuffle(unique.filter(item => seen.has(normalizeInspirationQuestion(item.question))), random);
  const ordered = [...unseen, ...previouslySeen];

  // Once the whole deck has been seen, do not restart with the last question.
  const last = normalizeInspirationQuestion(seenQuestions[seenQuestions.length - 1] || '');
  if (ordered.length > 1 && normalizeInspirationQuestion(ordered[0].question) === last) {
    const nextDifferent = ordered.findIndex(item => normalizeInspirationQuestion(item.question) !== last);
    if (nextDifferent > 0) {
      [ordered[0], ordered[nextDifferent]] = [ordered[nextDifferent], ordered[0]];
    }
  }
  return ordered;
}
